import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { EventApiService } from '../../services/event-api/EventApiService';
import {
  SportsEvent,
  Team,
  TicketStatus,
} from '../../types/Event';
import { resolveTeamLogoBackground } from '../../utils/resolveTeamLogoUrl';

type TicketSortField = 'date' | 'match' | 'total' | 'available' | 'reserved' | 'sold' | 'purchase' | 'profit';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-tickets-page',
  standalone: false,
  templateUrl: './tickets-page.component.html',
  styleUrl: './tickets-page.component.sass',
})
export class TicketsPageComponent implements OnInit {
  public events: SportsEvent[] = [];
  public page = 1;
  public pageSize = 20;
  public readonly pageSizeOptions = [
    20,
    50,
    100,
  ];
  public loading = false;
  public errorMessage: string | null = null;
  public sortField: TicketSortField = 'date';
  public sortDirection: SortDirection = 'asc';

  public constructor(
    private eventApi: EventApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadEvents();
  }

  public get pagedEvents(): SportsEvent[] {
    const startIndex = (this.page - 1) * this.pageSize;
    return this.sortedEvents.slice(startIndex, startIndex + this.pageSize);
  }

  public get sortedEvents(): SportsEvent[] {
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    return [
      ...this.events,
    ].sort((left, right) => this.compareByField(left, right, this.sortField) * direction);
  }

  public get totalPages(): number {
    return Math.max(Math.ceil(this.events.length / this.pageSize), 1);
  }

  public goToPage(page: number): void {
    this.page = Math.min(Math.max(page, 1), this.totalPages);
  }

  public setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
  }

  public setSort(field: TicketSortField): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortField = field;
    this.sortDirection = field === 'date' || field === 'match' ? 'asc' : 'desc';
  }

  public sortState(field: TicketSortField): string {
    if (this.sortField !== field) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public countTicketsByStatus(event: SportsEvent, status: TicketStatus): number {
    return event.tickets.filter((ticket) => ticket.status === status).length;
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public getLogoBackground(team: Team): string | null {
    return resolveTeamLogoBackground(team.logoUrl);
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadEvents(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.eventApi.getEvents();
      const events = this.unwrapResponse(response);
      if (events) {
        this.events = events.filter((event) => event.status === 'in_progress');
        this.page = Math.min(this.page, this.totalPages);
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить билеты.';
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private compareByField(left: SportsEvent, right: SportsEvent, field: TicketSortField): number {
    if (field === 'date') {
      return new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime();
    }
    if (field === 'match') {
      return this.getMatchName(left).localeCompare(this.getMatchName(right), 'ru');
    }
    if (field === 'total') {
      return left.summary.ticketsTotal - right.summary.ticketsTotal;
    }
    if (field === 'available') {
      return this.countTicketsByStatus(left, 'available') - this.countTicketsByStatus(right, 'available');
    }
    if (field === 'reserved') {
      return this.countTicketsByStatus(left, 'reserved') - this.countTicketsByStatus(right, 'reserved');
    }
    if (field === 'sold') {
      return this.countTicketsByStatus(left, 'sold') - this.countTicketsByStatus(right, 'sold');
    }
    if (field === 'purchase') {
      return left.summary.purchaseTotal - right.summary.purchaseTotal;
    }

    return left.summary.plannedProfitAfterExpenses - right.summary.plannedProfitAfterExpenses;
  }

  private getMatchName(event: SportsEvent): string {
    return `${event.homeTeam.name} - ${event.awayTeam.name}`;
  }
}
